import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Phone, Navigation, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useIntl, FormattedMessage } from "react-intl";

// Appointment status with i18n labels
const APPOINTMENT_STATUSES = {
  21: { labelId: "Appointment.Status.OnMyWay", color: "bg-blue-500" },
  22: { labelId: "Appointment.Status.NotStarted", color: "bg-gray-500" },
  23: { labelId: "Appointment.Status.InProgress", color: "bg-green-500" },
  24: { labelId: "Appointment.Status.Completed", color: "bg-emerald-500" },
  25: { labelId: "Appointment.Status.Declined", color: "bg-red-500" },
  26: { labelId: "Appointment.Status.Cancelled", color: "bg-orange-500" },
  28: { labelId: "Appointment.Status.Paused", color: "bg-yellow-500" },
};

// Get available status transitions based on current status
const getAvailableStatuses = (currentStatus) => {
  switch (currentStatus) {
    case 22:
      return [21, 25, 26];
    case 21:
      return [23, 28, 26];
    case 23:
      return [24, 28, 26];
    case 28:
      return [23, 24, 26];
    case 24:
    case 25:
    case 26:
      return [];
    default:
      return Object.keys(APPOINTMENT_STATUSES).map(Number);
  }
};

export function AppointmentCard({
  appointment,
  onViewDetails,
  onCall,
  onMaps,
  onStatusChange,
  isStatusUpdating = false,
}) {
  const intl = useIntl();

  // Format date and time
  const formatDate = (dateStr, timeStr) => {
    if (!dateStr)
      return intl.formatMessage({
        id: "Appointment.NoDate",
        defaultMessage: "No date",
      });

    try {
      const date = new Date(dateStr);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      let timeDisplay = "";
      if (timeStr) {
        const timeParts = timeStr.split(":");
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0]);
          const minutes = timeParts[1];
          const ampm = hours >= 12 ? "PM" : "AM";
          const displayHours = hours % 12 || 12;
          timeDisplay = `${displayHours}:${minutes} ${ampm}`;
        }
      }

      if (isToday) {
        return timeDisplay
          ? intl.formatMessage(
              { id: "Appointment.TodayAt", defaultMessage: "Today, {time}" },
              { time: timeDisplay }
            )
          : intl.formatMessage({
              id: "Appointment.Today",
              defaultMessage: "Today",
            });
      }

      const dateDisplay = date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
      return timeDisplay ? `${dateDisplay}, ${timeDisplay}` : dateDisplay;
    } catch {
      return dateStr;
    }
  };

  const getStatusInfo = (statusId) =>
    APPOINTMENT_STATUSES[statusId] || {
      labelId: "Appointment.Status.Unknown",
      color: "bg-gray-400",
    };

  const currentStatusInfo = getStatusInfo(
    appointment.status || appointment.appointmentStatusId
  );
  const availableStatuses = getAvailableStatuses(
    appointment.status || appointment.appointmentStatusId
  );

  const handleCall = () => {
    debugger;
    if (appointment.clientPhone || appointment.phoneNumber) {
      var number = appointment.clientPhone || appointment.phoneNumber;
      const cleanPhone = number.replace(/Cell\s+/, "");
      window.open(`tel:${cleanPhone}`, "_self");
    }
  };

  return (
    <Card>
      <CardContent className="!p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-left">
              {appointment.appointmentNumber ||
                intl.formatMessage(
                  {
                    id: "Appointment.DefaultTitle",
                    defaultMessage: "Appointment {id}",
                  },
                  { id: appointment.id }
                )}
            </h3>

            {appointment.customerName &&
              appointment.customerName !== appointment.clientName && (
                <p className="text-gray-700 dark:text-gray-300 text-sm text-left">
                  {appointment.customerName}
                </p>
              )}

            {appointment.address && (
              <p className="text-gray-600 dark:text-gray-400 text-sm text-left mb-1">
                {appointment.address}
              </p>
            )}

            <p className="text-gray-600 dark:text-gray-400 text-sm text-left">
              {formatDate(appointment.startDate, appointment.startTime)}
              {appointment.duration &&
                intl.formatMessage(
                  {
                    id: "Appointment.Duration",
                    defaultMessage: " • {minutes} min",
                  },
                  { minutes: appointment.duration }
                )}
            </p>

            {appointment.workOrderId && (
              <p className="text-gray-500 dark:text-gray-500 text-xs text-left mt-1">
                <FormattedMessage
                  id="Appointment.WorkOrder"
                  defaultMessage="Work Order: #{id}"
                  values={{ id: appointment.workOrderId }}
                />
              </p>
            )}
          </div>

          <div className="min-w-[130px]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-10 justify-start"
                  disabled={isStatusUpdating || availableStatuses.length === 0}
                >
                  <div className="flex items-center gap-2">
                    {isStatusUpdating ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <div
                        className={`w-3 h-3 rounded-full ${currentStatusInfo.color}`}
                      />
                    )}
                    <span className="text-xs">
                      <FormattedMessage id={currentStatusInfo.labelId} />
                    </span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {availableStatuses?.map((statusId) => {
                  const statusInfo = getStatusInfo(statusId);
                  return (
                    <DropdownMenuItem
                      key={statusId}
                      onClick={() => onStatusChange?.(statusId)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full ${statusInfo.color}`}
                        />
                        <FormattedMessage id={statusInfo.labelId} />
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex space-x-3">
          <Button
            onClick={() => onViewDetails?.(appointment.id)}
            className="flex-1 mobile-button"
          >
            <FormattedMessage
              id="Appointment.ViewDetails"
              defaultMessage="View Details"
            />
          </Button>

          {(appointment.clientPhone || appointment.phoneNumber) && (
            <Button
              variant="outline"
              size="icon"
              title={intl.formatMessage({
                id: "Appointment.CallClient",
                defaultMessage: "Call Client",
              })}
              className="mobile-button"
              onClick={handleCall}
              // onClick={() =>
              //   onCall?.(appointment.clientPhone || appointment.phoneNumber)
              // }
            >
              <Phone className="h-4 w-4" />
            </Button>
          )}

          {appointment.address && (
            <Button
              variant="outline"
              size="icon"
              title={intl.formatMessage({
                id: "Appointment.OpenInMaps",
                defaultMessage: "Open in Maps",
              })}
              className="mobile-button"
              onClick={() => onMaps?.(appointment.address)}
            >
              <Navigation className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
